/**
 * Home.
 *
 * Two views of the same set, in the order they get used: "Everything open"
 * answers "what do I do next" and is sorted globally; the boards below answer
 * "where does everything live" and are the same component the category page
 * uses, one per category, so the two screens cannot drift apart.
 *
 * The master list is capped so the top of the page is an answer rather than an
 * inventory. "Show all" expands it in place -- it is a display limit over data
 * already loaded, so expanding costs nothing.
 *
 * When a filter is active the boards are hidden. They exist to show the shape
 * of everything; against a filtered subset they would be a scatter of
 * mostly-empty tiles.
 *
 * Adding a task is a modal reached from a subcategory tile (which prefills
 * where it goes) or from the master list header (which does not). A permanent
 * form at the top of the page occupies that space every time you look at the
 * dashboard and is used a fraction of those times.
 */

import { useMemo, useState } from 'react'

import { api } from '@/api/client'
import type { Item, ItemCreatePayload, ItemPriority, Subcategory } from '@/api/types'
import { PRIORITIES } from '@/api/types'
import { AddTaskModal } from '@/components/AddTaskModal'
import { CategoryBoard } from '@/components/CategoryBoard'
import { DragHandle, SortableList } from '@/components/Sortable'
import { FilterBar } from '@/components/FilterBar'
import { PriorityBoard } from '@/components/PriorityBoard'
import { CalendarStrip } from '@/components/CalendarStrip'
import { FirstCategoryForm, InlineNameForm } from '@/components/TaxonomySetup'
import { useToast } from '@/components/Toast'
import { EmptyState, ErrorState, LoadingBlock, onTintPill } from '@/components/ui'
import { useCollapsed } from '@/hooks/useCollapsed'
import { useDashboard } from '@/hooks/useDashboard'
import { useFilters } from '@/hooks/useFilters'
import { usePriorityVisibility } from '@/hooks/usePriorityVisibility'
import { DEFAULT_CATEGORY_COLOR, readableTextOn } from '@/lib/color'
import { PRIORITY_LABEL, pluralize, todayIso } from '@/lib/format'

/** The master list's own header colour: the brand navy, treated like a
 *  category's own, with its text colour computed the same way. */
const MASTER_HEAD = {
  background: DEFAULT_CATEGORY_COLOR,
  text: readableTextOn(DEFAULT_CATEGORY_COLOR),
}

export function DashboardPage() {
  const toast = useToast()
  const { filters, active, update, toggle, clear, query } = useFilters()
  const {
    summary,
    categories,
    tags,
    items,
    total,
    loading,
    error,
    reload,
    updateItem,
    completeItem,
    addItem,
    clearOrder,
  } = useDashboard(query)
  const { isCollapsed, toggle: toggleCategory, setAll } = useCollapsed()
  const { visible: priorityVisible, toggle: togglePriority, hiddenCount } =
    usePriorityVisibility()
  const [modalOpen, setModalOpen] = useState(false)
  const [addingTo, setAddingTo] = useState<number | null>(null)
  const [addingPriority, setAddingPriority] = useState<ItemPriority | null>(null)

  const today = summary?.today ?? todayIso()
  const limit = summary?.item_limit ?? 15

  // The saved priority preference narrows the master list only. The boards
  // below always show everything, or a subcategory's own count would disagree
  // with the rows beneath it.
  const masterItems = useMemo(
    () => items.filter((item) => priorityVisible[item.priority]),
    [items, priorityVisible],
  )

  const pinned = useMemo(
    () => masterItems.filter((item) => item.manual_rank !== null),
    [masterItems],
  )

  const categoryIds = categories.map((category) => category.id)
  const allCollapsed = categoryIds.every((id) => isCollapsed(id))

  function openAdd(subcategoryId: number | null, priority: ItemPriority | null = null) {
    setAddingTo(subcategoryId)
    setAddingPriority(priority)
    setModalOpen(true)
  }

  async function saveSubcategoryStatus(sub: Subcategory, next: string | null) {
    try {
      await api.subcategories.update(sub.id, { status_note: next })
      await reload({ quiet: true })
    } catch {
      toast.error('Could not save that status.')
      await reload({ quiet: true })
    }
  }

  async function saveItemStatus(item: Item, next: string | null) {
    try {
      await api.items.update(item.id, { status_note: next })
      await reload({ quiet: true })
    } catch {
      toast.error('Could not save that status.')
      await reload({ quiet: true })
    }
  }

  async function reorderCategories(ids: number[]) {
    try {
      await api.categories.reorder(ids)
      await reload({ quiet: true })
    } catch {
      toast.error('Could not save that order.')
      await reload({ quiet: true })
    }
  }

  // Only one category's subcategories are ever sent, and the endpoint numbers
  // them from zero within that set. Ordering elsewhere is untouched because
  // listing sorts by (category_id, sort_order).
  async function reorderSubcategories(ids: number[]) {
    try {
      await api.subcategories.reorder(ids)
      await reload({ quiet: true })
    } catch {
      toast.error('Could not save that order.')
      await reload({ quiet: true })
    }
  }

  function changePriority(item: Item, priority: ItemPriority) {
    // updateItem is optimistic and reconciles against the server, so the item
    // jumps to its new tile immediately.
    updateItem(item.id, { priority })
  }

  async function createTask(payload: ItemCreatePayload) {
    await addItem(payload)
  }

  if (loading && !summary) return <LoadingBlock label="Loading your dashboard" />
  if (error && !summary) return <ErrorState message={error} onRetry={() => void reload()} />

  const hasTree = categories.some((category) => category.subcategories.length > 0)

  return (
    <div className="flex flex-col gap-5">
      {/* Loads on its own, after the page. Renders nothing when no calendar is
          connected, so it costs a request and no space to anyone not using it. */}
      <CalendarStrip />

      {hasTree && (
        <FilterBar
          filters={filters}
          active={active}
          categories={categories}
          tags={tags}
          matchCount={total}
          update={update}
          toggle={toggle}
          clear={clear}
        />
      )}

      {/* --- master list --- */}
      <section className="flex flex-col gap-3" aria-labelledby="master-heading">
        {/* Its own band, in the brand navy, styled like a category header so
            the page reads as a stack of coloured sections. */}
        <header
          style={{ backgroundColor: MASTER_HEAD.background, color: MASTER_HEAD.text }}
          className="flex flex-col gap-2 rounded-lg px-4 py-3 shadow-md sm:flex-row sm:flex-wrap sm:items-center sm:gap-3"
        >
          <h2
            id="master-heading"
            className="min-w-0 flex-1 text-xl font-bold tracking-tight sm:text-2xl"
          >
            {active ? 'Matching items' : 'Everything open'}
          </h2>

          {/* Its own row under the heading on a phone, beside it from sm up --
              the same arrangement the category bands use. */}
          <div className="flex flex-wrap items-center gap-2">
            {pinned.length > 0 && (
              <button
                type="button"
                className="text-xs font-semibold underline underline-offset-2 hover:opacity-80"
                onClick={() => void clearOrder(pinned.map((item) => item.id))}
                title="Remove the manual positions and return to automatic ordering"
              >
                Clear manual order ({pinned.length})
              </button>
            )}
            {masterItems.length > 0 && (
              <span className="pill border" style={onTintPill(MASTER_HEAD.text)}>
                {masterItems.length} {pluralize(masterItems.length, 'item')}
              </span>
            )}
            {hasTree && (
              <button
                type="button"
                onClick={() => openAdd(null)}
                style={{ color: MASTER_HEAD.text, borderColor: `${MASTER_HEAD.text}55` }}
                className="rounded-md border px-2 py-1 text-xs font-semibold transition-colors hover:bg-white/10"
              >
                + Task
              </button>
            )}
          </div>

          {/* Which priorities this section shows. A standing preference rather
              than a filter: it is remembered across sessions and narrows only
              this list, never the boards below. */}
          <div className="flex w-full flex-wrap items-center gap-1.5 border-t pt-2"
               style={{ borderColor: `${MASTER_HEAD.text}33` }}>
            <span className="mr-1 text-xs font-semibold" style={{ opacity: 0.8 }}>
              Show:
            </span>
            {PRIORITIES.map((priority) => {
              const on = priorityVisible[priority]
              return (
                <button
                  key={priority}
                  type="button"
                  onClick={() => togglePriority(priority)}
                  aria-pressed={on}
                  title={
                    on
                      ? `Hide ${PRIORITY_LABEL[priority].toLowerCase()} items from this section`
                      : `Show ${PRIORITY_LABEL[priority].toLowerCase()} items here`
                  }
                  className="pill border transition-opacity"
                  style={
                    on
                      ? {
                          backgroundColor: MASTER_HEAD.text,
                          color: MASTER_HEAD.background,
                          borderColor: MASTER_HEAD.text,
                        }
                      : {
                          backgroundColor: 'transparent',
                          color: MASTER_HEAD.text,
                          borderColor: `${MASTER_HEAD.text}55`,
                          opacity: 0.65,
                        }
                  }
                >
                  {PRIORITY_LABEL[priority]}
                </button>
              )
            })}
            {hiddenCount > 0 && (
              <span className="ml-1 text-xs" style={{ opacity: 0.75 }}>
                {hiddenCount} {pluralize(hiddenCount, 'priority', 'priorities')} hidden here
              </span>
            )}
          </div>
        </header>

        {masterItems.length === 0 ? (
          <div className="card overflow-hidden">
            {active ? (
              <EmptyState title="Nothing matches">
                No items match these filters.{' '}
                <button
                  type="button"
                  className="font-medium text-navy hover:underline"
                  onClick={clear}
                >
                  Clear them
                </button>{' '}
                to see everything again.
              </EmptyState>
            ) : hiddenCount > 0 && items.length > 0 ? (
              <EmptyState title="Nothing in the priorities you are showing">
                {items.length} open {pluralize(items.length, 'item')}{' '}
                {items.length === 1 ? 'is' : 'are'} hidden by the Show filters above.
              </EmptyState>
            ) : hasTree ? (
              <EmptyState title="Nothing open">
                Everything is done or archived. Add the next thing with the + Task button when
                it turns up.
              </EmptyState>
            ) : (
              <EmptyState title="Let's set up your first category">
                Docket organises work as Categories &rsaquo; Subcategories &rsaquo; Items. Items
                always live in a subcategory, so start with one of each &mdash; a category like
                &ldquo;Teaching&rdquo; and a subcategory per course.
                <span className="mt-4 block">
                  <FirstCategoryForm onCreated={() => reload({ quiet: true })} />
                </span>
              </EmptyState>
            )}
          </div>
        ) : (
          <PriorityBoard
            items={masterItems}
            today={today}
            visible={priorityVisible}
            onComplete={completeItem}
            onItemStatus={saveItemStatus}
            onPriorityChange={changePriority}
            onAddTask={(priority) => openAdd(null, priority)}
            limit={limit}
          />
        )}
      </section>

      {/* --- one board per category --- */}
      {categories.length > 0 && !active && (
        <>
          <div className="flex items-center justify-between px-1">
            <h2 className="text-sm font-semibold text-ink-2">By category</h2>
            <div className="flex items-center gap-4">
              <InlineNameForm
                label="New category"
                placeholder="Research"
                onSubmit={(name) => api.categories.create(name)}
                onDone={() => reload({ quiet: true })}
              />
              <button
                type="button"
                className="text-xs font-medium text-navy hover:underline"
                onClick={() => setAll(categoryIds, !allCollapsed)}
              >
                {allCollapsed ? 'Expand all' : 'Collapse all'}
              </button>
            </div>
          </div>

          <SortableList
            items={categories}
            getId={(category) => category.id}
            onReorder={(ids) => void reorderCategories(ids)}
            layout="column"
            className="flex flex-col gap-5"
          >
            {(category, drag) => (
              <div ref={drag.setNodeRef} style={drag.style}>
                <CategoryBoard
                  category={category}
                  items={items}
                  today={today}
                  collapsed={isCollapsed(category.id)}
                  onToggleCollapse={() => toggleCategory(category.id)}
                  onSubcategoryStatus={saveSubcategoryStatus}
                  onItemStatus={saveItemStatus}
                  onComplete={completeItem}
                  onPriorityChange={changePriority}
                  onAddTask={(subcategoryId) => openAdd(subcategoryId)}
                  onReorderSubcategories={(ids) => void reorderSubcategories(ids)}
                  dragHandle={
                    <DragHandle
                      handleProps={drag.handleProps}
                      label={`Reorder ${category.name}`}
                    />
                  }
                />
              </div>
            )}
          </SortableList>
        </>
      )}

      <AddTaskModal
        open={modalOpen}
        categories={categories}
        defaultSubcategoryId={addingTo}
        defaultPriority={addingPriority}
        onClose={() => setModalOpen(false)}
        onCreate={createTask}
      />
    </div>
  )
}
